import { getCookie } from '@/utils/cookie';
import { isSimpleAvRequired } from '@/utils/age_verification';
import { COOKIE_CONSENT_CONFIG } from './config';
import type { CookieChoices, ConsentData } from './types';
export class EntryFlowStore {
    isFlashing = false;
    showCookieConsent = false;
    showConsentAv = false;
    showAgeVerification = false;
    isCustomizingPreferences = false;
    cookieDomain: string;
    currentPolicyVersion: string;

    customCookieChoices: CookieChoices = {
        essential: true,
        analytics: this.getCookieValueByCategory('analytics'),
        functional: this.getCookieValueByCategory('functional'),
        targeting: this.getCookieValueByCategory('targeting'),
    };

    constructor(cookieDomain: string, currentPolicyVersion: string) {
        this.cookieDomain = cookieDomain;
        this.currentPolicyVersion = currentPolicyVersion;
    }

    init() {
        if (window.isCookieConsentNeeded()) {
            this.displayCookieConsent();

            if (isSimpleAvRequired()) {
                this.showConsentAv = true;
                return;
            }
        }

        if (window.isAgeVerificationRequired()) {
            this.displayAgeVerification();
        }
    }

    removeSafetyBlur() {
        window.getSafetyBlurElement()?.remove();
    }

    isCookieConsentNeeded() {
        return getCookie(COOKIE_CONSENT_CONFIG.SHOW_COOKIE_CONSENT_NAME) === '1';
    }

    private getConsentData(): ConsentData | null {
        const consentCookie = getCookie(COOKIE_CONSENT_CONFIG.COOKIE_NAME);
        if (!consentCookie) return null;

        try {
            return window.parseConsentCookie(consentCookie);
        } catch (error) {
            console.error('Failed to parse consent cookie:', error);
            return null;
        }
    }

    getConsentUuid(): string | null {
        const consentData = this.getConsentData();
        return consentData?.uuid;
    }

    getConsentUserId(): number | null {
        const consentData = this.getConsentData();
        return consentData?.user_id;
    }

    getConsentDate(): string | null {
        const consentData = this.getConsentData();
        if (!consentData?.timestamp) return null;

        try {
            const date = new Date(consentData.timestamp);

            if (date.getFullYear() < 2025) {
                return 'n/a';
            }

            return date.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
            });
        } catch (error) {
            console.error('Failed to format consent date:', error);
            return null;
        }
    }

    getCookieValueByCategory(category: string): boolean {
        return window.parseConsentCookie(getCookie(COOKIE_CONSENT_CONFIG.COOKIE_NAME))?.categories?.[category] || false;
    }

    getBannerVersion(): string | undefined {
        return this.currentPolicyVersion;
    }

    openManageCookiesModal() {
        this.showCookieConsent = true;
        this.isCustomizingPreferences = true;
    }

    displayCookieConsent() {
        this.showCookieConsent = true;
    }

    displayAgeVerification() {
        this.showAgeVerification = true;
    }

    reload() {
        window.location.reload();
    }

    triggerFlashing() {
        this.isFlashing = true;
        setTimeout(() => {
            this.isFlashing = false;
        }, COOKIE_CONSENT_CONFIG.FLASH_TIMEOUT_MS);
    }

    async setEssentialCookies() {
        if (isSimpleAvRequired()) await this.acceptSimpleAV(true);

        this.saveConsentToCookies(COOKIE_CONSENT_CONFIG.COOKIE_CHOICES.ESSENTIAL_ONLY);
        this.reload();
    }

    async setAllCookies() {
        if (isSimpleAvRequired()) await this.acceptSimpleAV(true);

        this.saveConsentToCookies(COOKIE_CONSENT_CONFIG.COOKIE_CHOICES.ALL_ACCEPTED);
        this.reload();
    }

    async setCustomCookies() {
        if (isSimpleAvRequired()) await this.acceptSimpleAV(true);

        this.saveConsentToCookies(this.customCookieChoices);
        this.reload();
    }

    async acceptSimpleAV(isPageReloading: boolean = false) {
        if (!isPageReloading) {
            this.showConsentAv = false;
            this.removeSafetyBlur();
        }

        const formData = new FormData();
        formData.append('id', '1');
        formData.append('data', '1');

        await fetch('/api/age_check', {
            method: 'POST',
            body: formData,
        });
    }

    async saveAndApply(choices: CookieChoices) {
        if (isSimpleAvRequired()) await this.acceptSimpleAV(false);
        this.saveConsentToCookies(choices);
        this.reload();
    }

    private saveConsentToCookies(choices: CookieChoices) {
        const consentData: ConsentData = {
            uuid: this.getConsentUuid(),
            timestamp: Date.now(),
            categories: choices,
            version: this.getBannerVersion(),
            user_id: this.getConsentUserId(),
        };

        // Base64 encode the consent data
        const encodedData = btoa(JSON.stringify(consentData));

        window.setCookie(COOKIE_CONSENT_CONFIG.COOKIE_NAME, encodedData, COOKIE_CONSENT_CONFIG.COOKIE_EXPIRY_DAYS, {
            domain: this.cookieDomain,
        });
    }
}
